import { useEffect, useMemo, useRef, useState } from "react";
import { tryApiGet } from "../api/client";
import type { HistogramBucket, WindowedStatsResponse } from "../api/types";
import { useAppData } from "../state/AppData";
import { parseTimestamp } from "../lib/format";
import { packetInProvenanceScope, PROVENANCE_SCOPE_LABEL } from '../lib/definitions/trust';
import { PROTOCOL_LABEL } from '../lib/definitions/protocols';
import { regionInScope } from "../lib/regions";

// Timeline (spec §6): a live 1h/6h/12h/24h window control over a traffic
// histogram, plus a scrub-to-replay mode. Two independent things share one
// control, per the spec's own instruction to make that split obvious rather
// than implicit — the window buttons always just resize the histogram; only
// clicking a bar enters replay.
//
// Histogram data source: GET /api/history/stats?since=&until=&bucket_s=,
// confirmed real via the backend's own OpenAPI schema (2026-09-20) — "Stats
// over an arbitrary window, bucketed — the ranged counterpart to
// /api/stats's live-only counters," per its own docstring. Falls back to
// bucketing the in-memory live packet buffer AppData already keeps (last
// ~300 packets, since this tab loaded) only if that call fails outright —
// the caption says so explicitly rather than presenting session-only data
// as equivalent to real backend history.
//
// Scrub-to-replay, by contrast, calls a genuinely real endpoint:
// `/api/packets?before=<iso>&limit=100` has persisted packet history since
// phase 1. Clicking a bar is therefore a real historical query, not a stub.
//
// Region filter (spec §10): confirmed via the backend's own OpenAPI schema
// (2026-09-20) that /api/history/stats takes only since/until/bucket_s — no
// region parameter exists. Rather than fake a region-scoped server window,
// selecting any region switches the histogram to the same client-side
// bucketing the "backend unavailable" fallback already used, filtered to
// packets whose origin node falls in a selected region (lib/regions.ts's
// regionInScope, shared with the map and PacketFeed) — and says so plainly
// in the caption, the same honesty convention StatusStrip already uses for
// protocol-scoped counts it can't get from the server. This is a real
// backend gap (no region-aware windowed-history endpoint), not something to
// approximate as equivalent to real history — flagged, not faked.
//
// Protocol filter (separated unless viewing the ALL
// page — the map/feed/legend all fully exclude the other protocol rather
// than dim it, and this histogram was found not to filter by protocol AT
// ALL, silently showing combined traffic regardless of the toggle). Same
// backend gap as region (no protocol param on /api/history/stats either),
// same fix: forces the client-side bucketing path, excluding non-matching
// packets outright — a bar chart only ever excludes, never dims, so this
// is the one surface where "separation" needed no new concept at all.
//
// Provenance scope ("this radio only" vs "everything we can see", added
// 2026-09-20): same gap, same fix — /api/history/stats has no provenance
// param either, so an active radio-only filter also forces client-side
// bucketing, excluding non-matching packets outright.
type WindowKey = "1h" | "6h" | "12h" | "24h";

const WINDOW_ORDER: WindowKey[] = ["1h", "6h", "12h", "24h"];
const WINDOW_MS: Record<WindowKey, number> = { "1h": 3_600_000, "6h": 6 * 3_600_000, "12h": 12 * 3_600_000, "24h": 24 * 3_600_000 };
// Bucket widths chosen to keep ~60-120 bars on screen per spec §6.
const BUCKET_SECONDS: Record<WindowKey, number> = { "1h": 60, "6h": 300, "12h": 600, "24h": 900 };

function bucketCountFor(w: WindowKey): number {
  return Math.round(WINDOW_MS[w] / (BUCKET_SECONDS[w] * 1000));
}

export function TimelinePanel() {
  const { packets, replay, enterReplay, exitReplay, nodes, selectedRegions, regionsForNode, protocolFilter, provenanceScope } = useAppData();
  const regionActive = selectedRegions.size > 0;
  const protocolActive = protocolFilter !== "all";
  const provenanceActive = provenanceScope === "connected_radio";
  const [windowKey, setWindowKey] = useState<WindowKey>("1h");
  const [backendBuckets, setBackendBuckets] = useState<HistogramBucket[] | null>(null);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 5000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setBackendBuckets(null);
    const untilMs = Date.now();
    const sinceMs = untilMs - WINDOW_MS[windowKey];
    const params = new URLSearchParams({
      since: new Date(sinceMs).toISOString(),
      until: new Date(untilMs).toISOString(),
      bucket_s: String(BUCKET_SECONDS[windowKey]),
    });
    tryApiGet<WindowedStatsResponse>(`/api/history/stats?${params.toString()}`)
      .then((res) => {
        if (cancelled) return;
        // Confirmed live, 2026-09-20: the bucket array is under `packets`,
        // each entry keyed `bucket_start` — see api/types.ts's header
        // comment for what this replaced (a guessed `buckets`/`ts` shape
        // that never matched, which is why this caption used to claim the
        // endpoint "isn't available" when it actually was).
        const list = res?.packets ?? null;
        setBackendBuckets(list && list.length > 0 ? list : null);
      })
      .catch(() => {
        if (!cancelled) setBackendBuckets(null);
      });
    return () => {
      cancelled = true;
    };
  }, [windowKey]);

  // A region, protocol, or provenance filter forces the client-side path
  // even if the backend window loaded fine — the backend has none of
  // these params to ask it for (see this file's header comment).
  const clientSideForced = regionActive || protocolActive || provenanceActive;
  const usingBackend = !clientSideForced && !!(backendBuckets && backendBuckets.length > 0);

  const buckets = useMemo(() => {
    if (!clientSideForced && backendBuckets && backendBuckets.length > 0) {
      return backendBuckets
        .map((b) => ({ t: parseTimestamp(b.bucket_start ?? null) ?? 0, count: b.count ?? 0 }))
        .filter((b) => b.t > 0)
        .sort((a, b) => a.t - b.t);
    }
    const bucketMs = BUCKET_SECONDS[windowKey] * 1000;
    const bars = bucketCountFor(windowKey);
    const start = now - bars * bucketMs;
    const counts = new Array(bars).fill(0);
    for (const p of packets) {
      // Protocol and provenance exclude outright (separation); region dims
      // elsewhere but here — a bar chart has no "dim," only "count or
      // don't" — also excludes. None of these is an approximation of what
      // the toggle means, just the only representation a histogram bar can
      // express.
      if (protocolActive && p.protocol !== protocolFilter) continue;
      if (!packetInProvenanceScope(p.trust_tier, provenanceScope)) continue;
      if (regionActive) {
        const fromNode = p.from_id != null ? nodes.get(String(p.from_id)) : undefined;
        if (!regionInScope(fromNode ?? null, { selectedCount: selectedRegions.size, regionsForNode })) continue;
      }
      const t = parseTimestamp(p.ts);
      if (t === null || t < start || t > now) continue;
      const idx = Math.floor((t - start) / bucketMs);
      if (idx >= 0 && idx < bars) counts[idx] += 1;
    }
    return counts.map((count, i) => ({ t: start + i * bucketMs, count }));
  }, [backendBuckets, packets, windowKey, now, regionActive, selectedRegions, regionsForNode, nodes, protocolActive, protocolFilter, provenanceScope, clientSideForced]);

  const maxCount = Math.max(1, ...buckets.map((b) => b.count));

  const bucketsRef = useRef(buckets);
  bucketsRef.current = buckets;

  function nearestBucketIndex(atMs: number): number {
    const list = bucketsRef.current;
    let best = -1;
    let bestDiff = Infinity;
    list.forEach((b, i) => {
      const diff = Math.abs(b.t - atMs);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = i;
      }
    });
    return best;
  }

  const replayIndex = replay.active && replay.atMs != null ? nearestBucketIndex(replay.atMs) : -1;

  // Playback: step forward through buckets on an interval; stop (and drop
  // back to live) once it reaches the newest bucket.
  useEffect(() => {
    if (!playing) return;
    const id = window.setInterval(() => {
      const list = bucketsRef.current;
      const cur = replay.atMs != null ? nearestBucketIndex(replay.atMs) : -1;
      const next = cur + 1;
      if (next >= list.length - 1) {
        setPlaying(false);
        return;
      }
      enterReplay(list[next].t);
    }, Math.max(150, 700 / speed));
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, speed, replay.atMs]);

  function onExitLive() {
    setPlaying(false);
    exitReplay();
  }

  return (
    <div className="timeline-panel">
      <div className="timeline-header">
        <div className="timeline-windows" role="group" aria-label="Timeline window">
          {WINDOW_ORDER.map((w) => (
            <button key={w} data-active={windowKey === w} onClick={() => setWindowKey(w)}>
              {w}
            </button>
          ))}
        </div>

        <div className="timeline-status">
          {replay.active ? (
            <span className="replay-badge">REPLAY</span>
          ) : (
            <span className="live-badge">
              <span className="ws-dot" /> LIVE
            </span>
          )}
          <span className="timeline-clock">
            {replay.active && replay.atMs != null ? new Date(replay.atMs).toLocaleString() : new Date(now).toLocaleTimeString()}
          </span>
        </div>

        {replay.active && (
          <div className="timeline-replay-controls">
            <button onClick={() => setPlaying((p) => !p)}>{playing ? "Pause" : "Play"}</button>
            <select value={speed} onChange={(e) => setSpeed(Number(e.target.value))} aria-label="Replay speed">
              <option value={1}>1x</option>
              <option value={2}>2x</option>
              <option value={4}>4x</option>
            </select>
            <button className="live-btn" onClick={onExitLive}>
              LIVE
            </button>
          </div>
        )}
      </div>

      <div className="histogram" role="img" aria-label="Packet traffic histogram">
        {buckets.map((b, i) => (
          <button
            key={b.t}
            className="histogram-bar"
            data-marked={i === replayIndex}
            style={{ height: `${4 + (b.count / maxCount) * 46}px` }}
            title={`${new Date(b.t).toLocaleTimeString()} — ${b.count} packet(s)`}
            onClick={() => enterReplay(b.t)}
          />
        ))}
      </div>

      <div className="timeline-caption">
        {/* 2026-09-27 first-installer review (part5d, item 4): reduced to a
            short label — no sentence explaining what history isn't
            available yet; the scope qualifiers (protocol/this radio
            only/region) are the same shared vocabulary used everywhere
            else on this page, not a new restatement. */}
        {clientSideForced
          ? (() => {
              const scopeParts = [
                protocolFilter !== "all" ? PROTOCOL_LABEL[protocolFilter] : null,
                provenanceActive ? PROVENANCE_SCOPE_LABEL.connected_radio : null,
                regionActive ? "region" : null,
              ].filter((x): x is string => x != null);
              const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
              return scopeParts.length > 0 ? `This session, ${scopeParts.map(lowerFirst).join(", ")}` : "This session";
            })()
          : usingBackend
            ? "Full history."
            : "This session"}
        {" Click a bar to replay from that moment; "}
        {replay.error && <span className="auth-error">{replay.error}</span>}
      </div>
    </div>
  );
}
